import { onboardingQuery, saveJobRole, teamRequestsQuery, resolveTeamRequest } from "@/lib/onboarding";
import { McpSettings } from "./Mcp";
import { useState } from "react";
import { Link, getRouteApi, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { WorkspaceRole } from "@tern/api-client";
import { PageHeader } from "@/components/AppShell";
import { SearchSettings, StorageSettings } from "@/components/DataSettings";
import { setLocale, type Locale } from "@/i18n";
import { applyTheme, getTheme, type Theme } from "@/lib/theme";
import {
  errorMessage,
  inviteMember,
  removeMember,
  revokeInvitation,
  setMemberRole,
  updateMe,
  workspaceInvitationsQuery,
  workspaceMembersQuery,
} from "@/lib/api";

const route = getRouteApi("/$ws");
const SECTIONS = ["account", "general", "members", "storage", "search", "mcp"] as const;
type Section = (typeof SECTIONS)[number];

export function SettingsPage() {
  const { t } = useTranslation();
  const { workspace } = route.useRouteContext();
  const params = useParams({ strict: false }) as { section?: string };
  const section: Section = SECTIONS.includes(params.section as Section) ? (params.section as Section) : "account";

  return (
    <>
      <PageHeader crumbs={[t("settings.title")]} />
      <div className="flex flex-1 overflow-hidden">
        <nav className="w-[200px] shrink-0 border-r border-border p-3">
          <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-faint">{t("settings.account")}</p>
          <SettingsLink ws={workspace.slug} section="account" active={section === "account"}>
            {t("settings.account")}
          </SettingsLink>
          <p className="mt-4 px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-faint">{workspace.name}</p>
          <SettingsLink ws={workspace.slug} section="general" active={section === "general"}>
            {t("workspace.general")}
          </SettingsLink>
          <SettingsLink ws={workspace.slug} section="members" active={section === "members"}>
            {t("workspace.members")}
          </SettingsLink>
          <SettingsLink ws={workspace.slug} section="mcp" active={section === "mcp"}>{t("mcp.title")}</SettingsLink>
          {workspace.role === "admin" && <>
            <p className="mt-5 px-2 pb-1 text-[11px] font-medium text-faint">{t("storage.section")}</p>
            <SettingsLink ws={workspace.slug} section="storage" active={section === "storage"}>{t("storage.title")}</SettingsLink>
            <SettingsLink ws={workspace.slug} section="search" active={section === "search"}>{t("searchSettings.title")}</SettingsLink>
          </>}
        </nav>
        <div className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-[640px]">
            {section === "mcp" && <McpSettings />}
            {section === "account" && <AccountSection />}
            {section === "general" && <GeneralSection />}
            {section === "members" && <MembersSection />}
            {section === "storage" && workspace.role === "admin" && <StorageSettings />}
            {section === "search" && workspace.role === "admin" && <SearchSettings />}
          </div>
        </div>
      </div>
    </>
  );
}

function SettingsLink({ ws, section, active, children }: { ws: string; section: Section; active: boolean; children: React.ReactNode }) {
  return (
    <Link
      to="/$ws/settings/$section"
      params={{ ws, section }}
      className={`flex h-7 items-center rounded-md px-2 text-[13px] ${active ? "bg-active text-fg" : "text-muted hover:bg-hover hover:text-fg"}`}
    >
      {children}
    </Link>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 text-[12px] font-medium text-muted">
      {label}
      {children}
    </label>
  );
}

function AccountSection() {
  const { t, i18n } = useTranslation();
  const { me } = route.useRouteContext();
  const qc = useQueryClient();
  const [name, setName] = useState(me.user.name);
  const save = useMutation({
    mutationFn: updateMe,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["me"] }),
  });
  const [theme, setTheme] = useState<Theme>(getTheme());

  return (
    <section className="flex flex-col gap-5">
      <h2 className="text-[16px] font-semibold">{t("settings.account")}</h2>
      <Field label={t("settings.displayName")}>
        <div className="flex gap-2">
          <input className="input h-8 max-w-[320px]" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn h-8" disabled={name.trim() === me.user.name || save.isPending} onClick={() => save.mutate({ name: name.trim() })}>
            {t("common.save")}
          </button>
        </div>
      </Field>
      <JobRoleSetting />
      <Field label={t("settings.language")}>
        <select
          className="input h-8 max-w-[200px]"
          value={i18n.language.startsWith("ko") ? "ko" : "en"}
          onChange={(e) => {
            const l = e.target.value as Locale;
            setLocale(l);
            save.mutate({ locale: l });
          }}
        >
          <option value="en">English</option>
          <option value="ko">한국어</option>
        </select>
      </Field>
      <Field label={t("settings.theme")}>
        <select
          className="input h-8 max-w-[200px]"
          value={theme}
          onChange={(e) => {
            const th = e.target.value as Theme;
            setTheme(th);
            applyTheme(th);
            save.mutate({ theme: th });
          }}
        >
          <option value="system">{t("palette.theme.system")}</option>
          <option value="dark">{t("palette.theme.dark")}</option>
          <option value="light">{t("palette.theme.light")}</option>
        </select>
      </Field>
      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          defaultChecked={me.user.email_notifications}
          onChange={(e) => save.mutate({ email_notifications: e.target.checked })}
        />
        {t("settings.emailNotifications")}
      </label>
      {save.isError && <p className="text-[12px] text-danger">{errorMessage(save.error)}</p>}
      {save.isSuccess && <p className="text-[12px] text-success">{t("settings.saved")}</p>}
    </section>
  );
}

function GeneralSection() {
  const { t } = useTranslation();
  const { workspace } = route.useRouteContext();
  return (
    <section className="flex flex-col gap-5">
      <h2 className="text-[16px] font-semibold">{t("workspace.general")}</h2>
      <Field label={t("workspace.name")}>
        <input className="input h-8 max-w-[320px]" defaultValue={workspace.name} readOnly />
      </Field>
      <Field label={t("workspace.slug")}>
        <input className="input h-8 max-w-[320px]" defaultValue={workspace.slug} readOnly />
      </Field>
    </section>
  );
}

function MembersSection() {
  const { t } = useTranslation();
  const { me, workspace } = route.useRouteContext();
  const qc = useQueryClient();
  const isAdmin = workspace.role === "admin";
  const members = useQuery(workspaceMembersQuery(workspace.slug));
  const invitations = useQuery({ ...workspaceInvitationsQuery(workspace.slug), enabled: isAdmin });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["workspace", workspace.slug] });
    void qc.invalidateQueries({ queryKey: ["me"] });
  };
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<WorkspaceRole>("member");
  const invite = useMutation({
    mutationFn: () => inviteMember(workspace.slug, email, role),
    onSuccess: () => {
      setEmail("");
      refresh();
    },
  });
  const revoke = useMutation({ mutationFn: (id: string) => revokeInvitation(workspace.slug, id), onSuccess: refresh });
  const changeRole = useMutation({
    mutationFn: (v: { user_id: string; role: WorkspaceRole }) => setMemberRole(workspace.slug, v.user_id, v.role),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: (user_id: string) => removeMember(workspace.slug, user_id), onSuccess: refresh });
  const anyError = invite.error ?? revoke.error ?? changeRole.error ?? remove.error;

  return (
    <section className="flex flex-col gap-6">
      <h2 className="text-[16px] font-semibold">{t("workspace.members")}</h2>

      {isAdmin && <JoinRequests slug={workspace.slug} />}
      {isAdmin && (
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            invite.mutate();
          }}
        >
          <Field label={t("workspace.inviteTitle")}>
            <input className="input h-8 w-[280px]" type="email" required value={email} placeholder={t("auth.emailPlaceholder")} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <select className="input h-8 w-[120px]" value={role} onChange={(e) => setRole(e.target.value as WorkspaceRole)}>
            <option value="member">{t("workspace.role.member")}</option>
            <option value="admin">{t("workspace.role.admin")}</option>
          </select>
          <button className="btn btn-primary h-8" type="submit" disabled={invite.isPending || !email}>
            {t("workspace.invite")}
          </button>
        </form>
      )}
      {anyError && <p className="text-[12px] text-danger">{errorMessage(anyError)}</p>}

      <ul className="divide-y divide-border rounded-lg border border-border">
        {members.data?.map((m) => (
          <li key={m.user_id} className="flex items-center gap-3 px-3 py-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-active text-[12px] font-medium uppercase">{m.name.slice(0, 1)}</span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium">
                {m.name} {m.user_id === me.user.id && <span className="text-faint">({t("common.you")})</span>}
              </div>
              <div className="truncate text-[12px] text-muted">{m.email}</div>
            </div>
            {isAdmin ? (
              <select
                className="input h-7 w-[110px]"
                value={m.role}
                onChange={(e) => changeRole.mutate({ user_id: m.user_id, role: e.target.value as WorkspaceRole })}
              >
                <option value="member">{t("workspace.role.member")}</option>
                <option value="admin">{t("workspace.role.admin")}</option>
              </select>
            ) : (
              <span className="text-[12px] text-muted">{t(`workspace.role.${m.role}`)}</span>
            )}
            {(isAdmin || m.user_id === me.user.id) && (
              <button className="btn btn-ghost h-7 text-muted hover:text-danger" onClick={() => remove.mutate(m.user_id)}>
                {m.user_id === me.user.id ? t("workspace.leave") : t("common.remove")}
              </button>
            )}
          </li>
        ))}
      </ul>

      {isAdmin && (invitations.data?.length ?? 0) > 0 && (
        <>
          <h3 className="text-[13px] font-medium text-muted">{t("workspace.pendingInvitations")}</h3>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {invitations.data?.map((inv) => (
              <li key={inv.id} className="flex items-center gap-3 px-3 py-2.5 text-[13px]">
                <span className="flex-1 truncate">{inv.email}</span>
                <span className="text-[12px] text-muted">{t(`workspace.role.${inv.role}`)}</span>
                <button className="btn btn-ghost h-7 text-muted hover:text-danger" onClick={() => revoke.mutate(inv.id)}>
                  {t("common.remove")}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function JoinRequests({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const requests = useQuery({ ...teamRequestsQuery(slug), refetchInterval: 15000 });
  const resolve = useMutation({ mutationFn: ({ id, approve }: { id: string; approve: boolean }) => resolveTeamRequest(id, approve), onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["account", "requests", slug] }); await qc.invalidateQueries({ queryKey: ["workspace", slug, "members"] }); } });
  if (!requests.data?.length) return null;
  return <div className="rounded-lg border border-border p-4"><h3 className="mb-3 text-[13px] font-medium">{t("onboarding.joinRequests")}</h3>{requests.data.map(request => <div key={request.id} className="flex items-center gap-3 border-t border-border py-3"><div className="min-w-0 flex-1"><p className="text-[13px]">{request.name}</p><p className="truncate text-[12px] text-muted">{request.email}</p></div><button className="btn btn-ghost text-muted" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: request.id, approve: false })}>{t("onboarding.decline")}</button><button className="btn btn-primary" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: request.id, approve: true })}>{t("onboarding.approve")}</button></div>)}{resolve.isError && <p className="mt-2 text-[12px] text-danger" role="alert">{errorMessage(resolve.error)}</p>}</div>;
}
function JobRoleSetting() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const setup = useQuery({ ...onboardingQuery, retry: false });
  const save = useMutation({ mutationFn: saveJobRole, onSuccess: () => qc.invalidateQueries({ queryKey: onboardingQuery.queryKey }) });
  if (!setup.data) return null;
  return <Field label={t("onboarding.jobRole")}><select className="input h-8 max-w-[240px]" value={setup.data.profile.job_role ?? ""} disabled={save.isPending} onChange={event => save.mutate(event.target.value)}>{["planner", "developer", "designer", "pm", "marketing", "operations", "leadership", "other"].map(role => <option key={role} value={role}>{t(`onboarding.roles.${role}`)}</option>)}</select>{save.isError && <p className="text-[12px] text-danger">{errorMessage(save.error)}</p>}</Field>;
}
